/**
 * Password Page
 *
 * Password management with strength indicator and requirements display.
 * Uses createPassword mutation (no current password needed — user is already authenticated).
 */

import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  useToast,
  Badge,
} from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useCreatePasswordMutation } from '@bloomcode/graphql';
import { Lock, Eye, EyeOff, Check, X, Shield } from '@bloomcode/icons';

interface PasswordRequirement {
  label: string;
  test: (pw: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw) => /\d/.test(pw) },
  {
    label: 'One special character',
    test: (pw) => /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  },
];

function getStrength(password: string): {
  label: string;
  className: string;
  percent: number;
} {
  const passed = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;
  if (passed <= 1) return { label: 'Weak', className: 'bg-destructive', percent: 20 };
  if (passed <= 3)
    return {
      label: 'Medium',
      className: 'bg-primary/60',
      percent: 60,
    };
  return { label: 'Strong', className: 'bg-primary', percent: 100 };
}

export default function PasswordPage() {
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [createPassword, { loading }] = useCreatePasswordMutation();

  const strength = useMemo(() => getStrength(newPassword), [newPassword]);
  const allRequirementsMet = PASSWORD_REQUIREMENTS.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRequirementsMet && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const result = await createPassword({
        variables: { input: { newPassword } },
      });

      if (result.data?.createPassword?.success) {
        toast({
          title: 'Password updated',
          description: 'Your password has been changed successfully.',
        });
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast({
          title: 'Failed to update password',
          description: result.data?.createPassword?.message || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Password - Bloomcode Account</title>
      </Helmet>
      <MetaTags title="Password Settings" description="Change your account password" />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Password</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">Password</h1>
          <p className="text-muted-foreground mt-1">Change your account password</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Change Password</CardTitle>
              </div>
              <CardDescription>
                Enter a new password for your account. You don&apos;t need your current password
                since you&apos;re already signed in.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* New Password */}
                <div className="space-y-2 max-w-md">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showNew ? 'Hide password' : 'Show password'}
                    >
                      {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Strength Indicator */}
                {newPassword.length > 0 && (
                  <div className="max-w-md space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Password strength</span>
                      <Badge variant={strength.label === 'Strong' ? 'default' : 'secondary'}>
                        {strength.label}
                      </Badge>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${strength.className}`}
                        style={{ width: `${strength.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Confirm Password */}
                <div className="space-y-2 max-w-md">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="text-sm text-destructive">Passwords do not match</p>
                  )}
                </div>

                {/* Requirements */}
                <div className="max-w-md">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Password Requirements</span>
                  </div>
                  <ul className="space-y-1.5">
                    {PASSWORD_REQUIREMENTS.map((req) => {
                      const met = req.test(newPassword);
                      return (
                        <li key={req.label} className="flex items-center gap-2 text-sm">
                          {met ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={met ? 'text-foreground' : 'text-muted-foreground'}>
                            {req.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <Button type="submit" disabled={!canSubmit}>
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
